import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import * as signalR from '@microsoft/signalr';

@Injectable({
	providedIn: 'root'
})
export class SignalrService {
	private readonly hubName = 'connectionHub';
	private connections = new Map<string, signalR.HubConnection>();

	isConnected(connectionId: string): boolean {
		const conn = this.connections.get(connectionId);
		return conn?.state === signalR.HubConnectionState.Connected;
	}

	connect(connectionId: string): Promise<void> {
		const existing = this.connections.get(connectionId);
		if (existing) {
			if (existing.state === signalR.HubConnectionState.Connected) {
				return Promise.resolve();
			}
			return existing.start();
		}

		const hubUrl = `${environment.whatsAppApi}/${this.hubName}?connectionId=${encodeURIComponent(connectionId)}`;

		const hubConnection = new signalR.HubConnectionBuilder()
			.withUrl(hubUrl)
			.withAutomaticReconnect()
			.configureLogging(signalR.LogLevel.Information)
			.build();

		this.connections.set(connectionId, hubConnection);
		return hubConnection.start();
	}

	disconnect(connectionId: string): Promise<void> {
		const connection = this.connections.get(connectionId);
		if (!connection) {
			return Promise.resolve();
		}

		this.connections.delete(connectionId);
		return connection.stop();
	}

	disconnectAll(): Promise<void> {
		const stops = Array.from(this.connections.values()).map(c => c.stop());
		this.connections.clear();
		return Promise.all(stops).then(() => undefined);
	}

	on<TPayload = any>(connectionId: string, methodName: string, newMethod: (payload: TPayload) => void): void {
		this.connections.get(connectionId)?.on(methodName, newMethod);
	}

	off(connectionId: string, methodName: string): void {
		this.connections.get(connectionId)?.off(methodName);
	}

	invoke<TResult = any>(connectionId: string, methodName: string, ...args: any[]): Promise<TResult> {
		const connection = this.connections.get(connectionId);
		if (!connection) {
			return Promise.reject('SignalR connection is not started');
		}

		return connection.invoke<TResult>(methodName, ...args);
	}
}

